import React, { useEffect, useMemo, useRef } from 'react';

import type { Property } from 'csstype';

import { useImageElement } from './hooks/useImageElement';
import type {
  AnnotationHoverState,
  AnnotationsState,
  DegreeRotations,
  IdType,
} from './types';
import type {
  AnnotationsProps,
  BoundingBoxAnnotationProps,
  LineAnnotationProps,
  PointAnnotationProps,
  SegmentationAnnotationProps,
} from './types/props';
import { drawBox } from './utils/box';
import { rescaleCanvas } from './utils/canvas';
import {
  getClientCoordinatesOnCanvas,
  isHoveringOnBoxAnnotation,
  isHoveringOnLineAnnotation,
  isHoveringOnPointAnnotation,
  isHoveringOnSegmentationAnnotation,
} from './utils/interactions';
import { drawLine, drawPoint } from './utils/line';
import { drawOval } from './utils/oval';
import { drawSegmentation } from './utils/segmentations';

const getShapesWithDefaults = (input: {
  boundingBoxes: BoundingBoxAnnotationProps[];
  lineCoordinates: LineAnnotationProps[];
  ovalCoordinates: BoundingBoxAnnotationProps[];
  segmentationCoordinates: SegmentationAnnotationProps[];
  pointCoordinates: PointAnnotationProps[];
}): AnnotationsState => {
  const defaultStyles = {
    box: { strokeColor: 'red', strokeWidth: 1 },
    line: {
      strokeColor: 'green',
      strokeWidth: 3,
      strokeDashArray: [5, 3],
      showHandles: true,
      handleRadius: 2,
    },
    oval: { strokeColor: 'blue', strokeWidth: 1 },
    segmentation: {
      strokeColor: 'red',
      strokeWidth: 1,
      fillColor: 'rgba(255, 0, 0, 0.2)',
    },
    point: { strokeColor: 'red', strokeWidth: 1 },
  };

  const shapesWithDefaults = {
    boundingBoxes: input.boundingBoxes.map((box) => ({
      ...box,
      styles: box.styles ?? defaultStyles.box,
    })),
    lines: input.lineCoordinates.map((line) => ({
      ...line,
      styles: line.styles ?? defaultStyles.line,
    })),
    ovals: input.ovalCoordinates.map((oval) => ({
      ...oval,
      styles: oval.styles ?? defaultStyles.oval,
    })),
    segmentations: input.segmentationCoordinates.map((segmentation) => ({
      ...segmentation,
      styles: segmentation.styles ?? defaultStyles.segmentation,
    })),
    points: input.pointCoordinates.map((point) => ({
      ...point,
      styles: point.styles ?? defaultStyles.point,
    })),
  };

  return shapesWithDefaults;
};

const drawFindings = (
  ctx: CanvasRenderingContext2D,
  scale: number,
  shapesWithDefaults: AnnotationsState,
) => {
  shapesWithDefaults.boundingBoxes.forEach((box) => {
    drawBox({
      coordintate: box,
      styles: box.styles,
      context: ctx,
      offset: { dx: 0, dy: 0 },
      scale,
    });
  });

  shapesWithDefaults.lines.forEach((line) => {
    drawLine({
      coordintate: line,
      styles: line.styles,
      context: ctx,
      offset: { dx: 0, dy: 0 },
      scale,
    });
  });

  shapesWithDefaults.ovals.forEach((oval) => {
    drawOval({
      coordintate: oval,
      styles: oval.styles,
      context: ctx,
      offset: { dx: 0, dy: 0 },
      scale,
    });
  });

  shapesWithDefaults.segmentations.forEach((segmentation) => {
    drawSegmentation({
      coordintate: segmentation,
      styles: segmentation.styles,
      context: ctx,
      scale,
    });
  });

  shapesWithDefaults.points.forEach((point) => {
    drawPoint({
      coordintate: point,
      styles: point.styles,
      context: ctx,
      scale,
    });
  });
};

type Props = {
  id: IdType;
  height?: number;
  width?: number;
  degreeRotation?: DegreeRotations;
  annotations: AnnotationsProps;
  imageSrc: string;
  onAnnotationHover?: (hoverState: AnnotationHoverState) => void;
  _devDrawingCanvasBorder?: Property.Border<string | number>;
  _devShowMousePos?: boolean;
  imageFetchHeaders?: HeadersInit;
  borderBackgroundColor?: string;
};

const Annotator = ({
  imageSrc,
  annotations,
  height = 300,
  width = 500,
  onAnnotationHover,
  _devDrawingCanvasBorder,
  _devShowMousePos = false,
  imageFetchHeaders,
  borderBackgroundColor = 'black',
}: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [mousePos, setMousePos] = React.useState<{
    x: number;
    y: number;
  } | null>(null);

  const shapesWithDefaults = useMemo(() => {
    return getShapesWithDefaults({
      boundingBoxes: annotations.boundingBoxes ?? [],
      lineCoordinates: annotations.lines ?? [],
      ovalCoordinates: annotations.ovals ?? [],
      segmentationCoordinates: annotations.segmentations ?? [],
      pointCoordinates: annotations.points ?? [],
    });
  }, [annotations]);

  const { imageDimensions, imageLoaded, scale } = useImageElement({
    imageFetchHeaders,
    imageSrc,
    height,
    width,
    imgRef,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');

    const drawShapes = () => {
      if (!canvas || !imgRef.current || !imageLoaded || !ctx) return;

      ctx.clearRect(0, 0, imageDimensions.width, imageDimensions.height);
      rescaleCanvas(canvas, ctx, imageDimensions);
      drawFindings(ctx, scale, shapesWithDefaults);
    };

    drawShapes();
  }, [canvasRef, imageLoaded, imageDimensions, scale, shapesWithDefaults]);

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect() as DOMRect;
    const interactionCoordinates = getClientCoordinatesOnCanvas(e, scale, rect);

    setMousePos(interactionCoordinates);

    const isHoveringOnBox = isHoveringOnBoxAnnotation(
      shapesWithDefaults.boundingBoxes,
      interactionCoordinates,
      2,
    );

    const isHoveringOnOval = isHoveringOnBoxAnnotation(
      shapesWithDefaults.ovals,
      interactionCoordinates,
      2,
    );

    const isHoveringOnLine = isHoveringOnLineAnnotation(
      shapesWithDefaults.lines,
      interactionCoordinates,
      2,
    );

    const isHoveringOnSegmentation = isHoveringOnSegmentationAnnotation(
      shapesWithDefaults.segmentations,
      interactionCoordinates,
      2,
    );

    const isHoveringOnPoint = isHoveringOnPointAnnotation(
      shapesWithDefaults.points,
      interactionCoordinates,
      2,
    );

    onAnnotationHover?.({
      boundingBox: isHoveringOnBox,
      oval: isHoveringOnOval,
      line: isHoveringOnLine,
      segmentation: isHoveringOnSegmentation,
      point: isHoveringOnPoint,
    });
  };

  const handleMouseLeave = () => {
    setMousePos(null);
  };

  return (
    <div
      style={{
        position: 'relative',
        height,
        width,
        backgroundColor: borderBackgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        ref={imgRef}
        height={imageDimensions.height}
        width={imageDimensions.width}
        style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
      />
      <canvas
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: 1,
          border: _devDrawingCanvasBorder,
        }}
        height={imageDimensions.height}
        width={imageDimensions.width}
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
      {_devShowMousePos && mousePos && (
        <div
          style={{
            position: 'absolute',
            bottom: 5,
            right: 5,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          x: {Math.round(mousePos.x)}, y: {Math.round(mousePos.y)}
        </div>
      )}
    </div>
  );
};

export default Annotator;
